import { cancelled, delay, put, race } from 'redux-saga/effects';

// import { dummyLiveTesting } from 'app/dealer/shared/utils/test/test-helpers/dummyExamples';
import { abortableClientApiCall } from 'app/dealer/actions';

/**
 * @description
 * resuable Saga API that implements:
 *  - inflight ajax aborting
 *  - delayed 200ms dispatch for loading
 *    to avoid quick flashing loading spinners in the UI
 *
 * accepts:
 *  - a url for the api call
 *  - custom action creators for loading and cancelled sagas
 *
 * returns a result or an error
 */

/**
 * @param {string} url
 * @param {import('../../../sagas/types').ApiProgressActions} apiProgressActions
 * @returns {{result: any} | {error: Error|any}}
 */
export const defaultApiProgressActions = {
  apiLoading: () => ({ type: 'DEFAULT_API_LOADING' }),
  apiCancelled: () => ({ type: 'DEFAULT_API_CANCELLED' }),
};

export function* apiCallSaga(
  url,
  apiProgressActions = defaultApiProgressActions
) {
  let abortController;
  let promise;
  try {
    //----------------------------------------------------------------------------
    /**
     * @description dummyData
     * block below can be uncommented to display dummy data
     * on a live environment
     *
     * e.g.
     * we can use it internal demonstration purposes on a PR environment
     * so that we can display full data that is not private to a dealer network
     */
    //
    // const dummyData = dummyLiveTesting(url);
    // if (dummyData) return dummyData;
    //----------------------------------------------------------------------------

    // returns a promise and an instance of an abort controller
    ({ promise, abortController } = yield put(
      abortableClientApiCall(url, {}, !process.env.REACT_APP_MOCK_SERVER)
    ));

    // if the response comes back in under 200ms, don't show loading spinner
    const { result } = yield race({
      result: promise,
      timeout: delay(200),
    });
    // if response comes back, return result
    if (result) {
      return { result };
    }
    // if response takes longer than 200ms, set state to loading
    yield put(apiProgressActions.apiLoading());
    const slowResult = yield promise;

    return { result: slowResult };
  } catch (error) {
    return { error };
  } finally {
    if (yield cancelled()) {
      if (abortController) {
        abortController.abort();
      }
      yield put(apiProgressActions.apiCancelled());
    }
  }
}
